import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { requestMapDeliveriesFullFetch } from "./map/hooks";
import { useTelemetry } from "../shared/useTelemetry";

/**
 * Request to mark all addresses along a road as delivered
 */
export interface BulkDeliveryByRoadRequest {
    campaignId: string;
    roadSegmentId: number;
    flyerTypeIds: string[];
    bufferM?: number;
    relaxedStreetBufferM?: number;
    clientRequestId?: string;
}

/**
 * Request to mark specific addresses as delivered
 */
export interface BulkDeliveryByAddressesRequest {
    campaignId: string;
    addressPointIds: string[];
    flyerTypeIds: string[];
    clientRequestId?: string;
}

/**
 * Response from bulk delivery operations
 */
export interface BulkDeliveryResponse {
    createdCount: number;
    skippedCount: number;
    errors: string[];
}

/**
 * Previews the number of addresses that would be affected by bulk delivery
 */
export const previewAddressCountByRoad = async (
    campaignId: string,
    roadSegmentId: number,
    bufferM: number = 40.0,
    relaxedStreetBufferM?: number
): Promise<number> => {
    const params = new URLSearchParams({
        campaignId,
        roadSegmentId: roadSegmentId.toString(),
        bufferM: bufferM.toString()
    });
    if (relaxedStreetBufferM !== undefined) {
        params.append("relaxedStreetBufferM", relaxedStreetBufferM.toString());
    }
    const { data } = await apiClient.get<number>(`/deliveries/by-road/preview?${params.toString()}`);
    return data;
};

/**
 * Records deliveries for all addresses along a road segment
 */
export const recordDeliveriesByRoad = async (
    request: BulkDeliveryByRoadRequest
): Promise<BulkDeliveryResponse> => {
    const { data } = await apiClient.post<BulkDeliveryResponse>(
        "/deliveries/by-road",
        {
            campaignId: request.campaignId,
            roadSegmentId: request.roadSegmentId,
            flyerTypeIds: request.flyerTypeIds,
            bufferM: request.bufferM ?? 40.0,
            relaxedStreetBufferM: request.relaxedStreetBufferM,
            clientRequestId: request.clientRequestId
        }
    );
    return data;
};

/**
 * Records deliveries for specific addresses
 */
export const recordDeliveriesByAddresses = async (
    request: BulkDeliveryByAddressesRequest
): Promise<BulkDeliveryResponse> => {
    const { data } = await apiClient.post<BulkDeliveryResponse>(
        "/deliveries/by-addresses",
        {
            campaignId: request.campaignId,
            addressPointIds: request.addressPointIds,
            flyerTypeIds: request.flyerTypeIds,
            clientRequestId: request.clientRequestId
        }
    );
    return data;
};

/**
 * Undoes deliveries for all addresses along a road segment
 */
export const undoDeliveriesByRoad = async (
    campaignId: string,
    roadSegmentId: number
): Promise<{ deletedCount: number }> => {
    const { data } = await apiClient.delete<{ deletedCount: number }>(
        `/deliveries/by-road?campaignId=${campaignId}&roadSegmentId=${roadSegmentId}`
    );
    return data;
};

/**
 * Undoes deliveries for specific addresses
 */
export const undoDeliveriesByAddresses = async (
    campaignId: string,
    addressPointIds: string[]
): Promise<{ deletedCount: number }> => {
    const { data } = await apiClient.delete<{ deletedCount: number }>(
        `/deliveries/by-addresses?campaignId=${campaignId}`,
        {
            data: addressPointIds
        }
    );
    return data;
};

/**
 * Gets all deliveries for a specific address in a campaign
 */
export interface DeliveryResponse {
    id: string;
    campaignId: string;
    /** Set on GET .../by-address/{id}/all for list display */
    campaignName?: string | null;
    addressPointId: string;
    /** Null for campaign-only flyers that have no linked global flyer type */
    flyerTypeId?: string | null;
    campaignFlyerId?: string | null;
    /** Set on GET .../by-address/{id}/all for list display */
    flyerName?: string | null;
    recordedByUserId: string;
    campaignActivityId?: string | null;
    distributorAssignmentId?: string | null;
    deliveredAt: string;
    quantity?: number | null;
    source?: string | null;
    device?: string | null;
    appVersion?: string | null;
    clientRequestId?: string | null;
    route?: Array<{ latitude: number; longitude: number }> | null;
}

/** True if this delivery row matches the map's selected flyer (FlyerType id or CampaignFlyer id). */
export function deliveryMatchesSelectedFlyerType(
    d: DeliveryResponse,
    selectedFlyerTypeId: string
): boolean {
    return (
        (d.flyerTypeId != null && d.flyerTypeId === selectedFlyerTypeId) ||
        (d.campaignFlyerId != null && d.campaignFlyerId === selectedFlyerTypeId)
    );
}

export const getDeliveriesByAddress = async (
    addressPointId: string,
    campaignId: string
): Promise<DeliveryResponse[]> => {
    const { data } = await apiClient.get<DeliveryResponse[]>(
        `/deliveries/by-address/${addressPointId}?campaignId=${campaignId}`
    );
    return data;
};

export const getDeliveriesByAddressAllCampaigns = async (addressPointId: string): Promise<DeliveryResponse[]> => {
    const { data } = await apiClient.get<DeliveryResponse[]>(`/deliveries/by-address/${addressPointId}/all`);
    return data;
};

/**
 * Deletes a single delivery
 */
export const deleteDelivery = async (deliveryId: string): Promise<void> => {
    await apiClient.delete(`/deliveries/${deliveryId}`);
};

/**
 * Session statistics for deliveries
 */
export interface DeliverySessionStatistics {
    clientRequestId: string;
    deliveryCount: number;
    addressCount: number;
    totalQuantity: number;
    firstDeliveredAt?: string | null;
    lastDeliveredAt?: string | null;
    campaignId?: string | null;
    campaignName?: string | null;
}

/**
 * Gets statistics for a delivery session
 */
export const getSessionStatistics = async (
    clientRequestId: string
): Promise<DeliverySessionStatistics> => {
    const { data } = await apiClient.get<DeliverySessionStatistics>(
        `/deliveries/statistics/session?clientRequestId=${encodeURIComponent(clientRequestId)}`
    );
    return data;
};

/**
 * Today's deliveries statistics
 */
export interface TodayDeliveriesStatistics {
    deliveryCount: number;
    addressCount: number;
    totalQuantity: number;
    firstDeliveredAt?: string | null;
    lastDeliveredAt?: string | null;
    campaignId?: string | null;
    campaignName?: string | null;
}

/**
 * Gets statistics for today's deliveries
 */
export const getTodayDeliveriesStatistics = async (
    campaignId?: string
): Promise<TodayDeliveriesStatistics> => {
    const params = campaignId ? `?campaignId=${campaignId}` : "";
    const { data } = await apiClient.get<TodayDeliveriesStatistics>(
        `/deliveries/statistics/today${params}`
    );
    return data;
};

export interface DailyDeliveryShareDto {
    date: string;
    totalQuantity: number;
    deliveryCount: number;
}

/** Personal delivery stats + optional week/month/year goals (flyer quantity). */
export interface UserDeliveryShareStatistics {
    lifetimeTotalQuantity: number;
    lifetimeDeliveryCount: number;
    todayTotalQuantity: number;
    todayDeliveryCount: number;
    lastSevenDaysTotalQuantity: number;
    lastSevenDays: DailyDeliveryShareDto[];
    weekGoalQuantity?: number | null;
    monthGoalQuantity?: number | null;
    yearGoalQuantity?: number | null;
    calendarWeekTotalQuantity: number;
    calendarMonthTotalQuantity: number;
    calendarYearTotalQuantity: number;
}

export interface UpdateUserDeliveryShareGoalsRequest {
    weekGoalQuantity?: number | null;
    monthGoalQuantity?: number | null;
    yearGoalQuantity?: number | null;
}

const browserTimeZoneParams = (): URLSearchParams => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
    const utcOffsetMinutes = String(-new Date().getTimezoneOffset());
    return new URLSearchParams({
        timeZone,
        utcOffsetMinutes
    });
};

export const getMyDeliveryShareStatistics = async (): Promise<UserDeliveryShareStatistics> => {
    const params = browserTimeZoneParams();
    const { data } = await apiClient.get<UserDeliveryShareStatistics>(
        `/deliveries/statistics/me?${params.toString()}`
    );
    return data;
};

export const putMyDeliveryShareGoals = async (
    body: UpdateUserDeliveryShareGoalsRequest
): Promise<UserDeliveryShareStatistics> => {
    const params = browserTimeZoneParams();
    const { data } = await apiClient.put<UserDeliveryShareStatistics>(
        `/deliveries/statistics/me/goals?${params.toString()}`,
        body
    );
    return data;
};

/**
 * React Query mutation hook for recording deliveries by road
 */
export const useRecordDeliveriesByRoad = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: recordDeliveriesByRoad,
        onSuccess: (response) => {
            telemetry.track("delivery.bulkByRoad", {
                createdCount: response.createdCount,
                skippedCount: response.skippedCount
            });
            requestMapDeliveriesFullFetch();
            // Only invalidate the deliveries sub-query — static address geometry never changes
            // on a delivery write. Invalidating the full ["map","addresses"] prefix would also
            // refetch the static query; its faster response would re-trigger computeCombinedAddresses
            // with stale delivery data and overwrite optimistic map updates before the delivery
            // fetch completes.
            queryClient.invalidateQueries({
                queryKey: ["map", "addresses", "deliveries"],
                refetchType: "active"
            });
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("delivery.bulkByRoadFailed", error);
        }
    });
};

/**
 * React Query mutation hook for recording deliveries by addresses
 */
export const useRecordDeliveriesByAddresses = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: recordDeliveriesByAddresses,
        onSuccess: (response) => {
            telemetry.track("delivery.bulkByAddresses", {
                createdCount: response.createdCount,
                skippedCount: response.skippedCount
            });
            requestMapDeliveriesFullFetch();
            // Only invalidate the deliveries sub-query (see useRecordDeliveriesByRoad comment)
            queryClient.invalidateQueries({
                queryKey: ["map", "addresses", "deliveries"],
                refetchType: "active"
            });
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("delivery.bulkByAddressesFailed", error);
        }
    });
};

/**
 * React Query mutation hook for undoing deliveries by road
 */
export const useUndoDeliveriesByRoad = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: ({ campaignId, roadSegmentId }: { campaignId: string; roadSegmentId: number }) =>
            undoDeliveriesByRoad(campaignId, roadSegmentId),
        onSuccess: (response) => {
            telemetry.track("delivery.undoByRoad", {
                deletedCount: response.deletedCount
            });
            requestMapDeliveriesFullFetch();
            // Only refetch deliveries — static address data doesn't change on undo
            queryClient.refetchQueries({ queryKey: ["map", "addresses", "deliveries"] });
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("delivery.undoByRoadFailed", error);
        }
    });
};

/**
 * React Query mutation hook for undoing deliveries by addresses
 */
export const useUndoDeliveriesByAddresses = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: ({ campaignId, addressPointIds }: { campaignId: string; addressPointIds: string[] }) =>
            undoDeliveriesByAddresses(campaignId, addressPointIds),
        onSuccess: (response) => {
            telemetry.track("delivery.undoByAddresses", {
                deletedCount: response.deletedCount
            });
            requestMapDeliveriesFullFetch();
            // Only refetch deliveries — static address data doesn't change on undo
            queryClient.refetchQueries({ queryKey: ["map", "addresses", "deliveries"] });
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("delivery.undoByAddressesFailed", error);
        }
    });
};

/**
 * React Query hook for fetching deliveries by address
 */
export const useDeliveriesByAddress = (addressPointId: string | undefined, campaignId: string | undefined) => {
    const telemetry = useTelemetry();

    const query = useQuery<DeliveryResponse[], Error>({
        queryKey: ["deliveries", "by-address", addressPointId, campaignId],
        queryFn: () => {
            if (!addressPointId || !campaignId) {
                throw new Error("Address point ID and campaign ID are required");
            }
            return getDeliveriesByAddress(addressPointId, campaignId);
        },
        enabled: !!addressPointId && !!campaignId,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
        retry: false // Don't retry on error to avoid blocking
    });

    // Track errors separately
    useEffect(() => {
        if (query.isError && query.error) {
            telemetry.trackError("delivery.getByAddressFailed", query.error);
        }
    }, [query.isError, query.error, telemetry]);

    return query;
};

export const useDeliveriesByAddressAll = (addressPointId: string | undefined) => {
    const telemetry = useTelemetry();

    const query = useQuery<DeliveryResponse[], Error>({
        queryKey: ["deliveries", "by-address-all", addressPointId],
        queryFn: () => {
            if (!addressPointId) {
                throw new Error("Address point ID is required");
            }
            return getDeliveriesByAddressAllCampaigns(addressPointId);
        },
        enabled: !!addressPointId,
        staleTime: 30 * 1000,
        gcTime: 2 * 60 * 1000,
        retry: false
    });

    useEffect(() => {
        if (query.isError && query.error) {
            telemetry.trackError("delivery.getByAddressAllFailed", query.error);
        }
    }, [query.isError, query.error, telemetry]);

    return query;
};

/**
 * React Query mutation hook for deleting a single delivery
 */
export const useDeleteDelivery = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: deleteDelivery,
        onSuccess: () => {
            telemetry.track("delivery.deleted");
            requestMapDeliveriesFullFetch();
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
            // Do not invalidate ["map", "addresses"] here: MapView undo may delete several rows in parallel;
            // each onSuccess would refetch and stale responses can overwrite the optimistic undelivered state
            // (address stuck green). Callers batch-delete should invalidate/refetch map addresses once afterward.
        },
        onError: (error: unknown) => {
            telemetry.trackError("delivery.deleteFailed", error);
        }
    });
};